import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getOrder, getOrderLocation } from 'features/delivery/services/orderApi';
import { queryKeys } from 'core/query/queryKeys';
import { connectOrderEvents } from 'core/realtime/orderEventsClient';
import { handleOrderRealtimeEvent } from 'core/realtime/handleOrderRealtimeEvent';
import {
  POLL_ORDER_BACKOFF_MS,
  POLL_ORDER_DEFAULT_MS,
  POLL_ORDER_LOCATION_MS,
  POLL_ORDER_SEARCHING_MS,
} from 'core/realtime/realtimeConfig';
import { ENABLE_ORDER_SSE } from 'core/config/maps';
import { isRateLimited } from 'core/api/rateLimit';
import type { Order, OrderStatus } from 'types/delivery.types';

const TRACKABLE_STATUSES: OrderStatus[] = [
  'accepted',
  'picked_up',
  'in_transit',
];

function isSearching(order: Order): boolean {
  const driverUnset = order.driverId == null || order.driverId === '';
  const vehicleUnset = order.vehicleId == null || order.vehicleId === '';
  return order.status === 'pending' && driverUnset && vehicleUnset;
}

function isTrackable(order: Order | undefined): boolean {
  return Boolean(order && TRACKABLE_STATUSES.includes(order.status));
}

function pollInterval(order: Order | undefined): number | false {
  if (!order) return POLL_ORDER_DEFAULT_MS;
  if (
    order.status === 'delivered' ||
    order.status === 'cancelled' ||
    order.status === 'no_provider'
  ) {
    return false;
  }
  if (isRateLimited()) return POLL_ORDER_BACKOFF_MS;
  // Poll fast while waiting for payment webhook confirmation or driver search
  if (order.status === 'awaiting_payment' || isSearching(order)) return POLL_ORDER_SEARCHING_MS;
  return POLL_ORDER_DEFAULT_MS;
}

function locationPollInterval(order: Order | undefined): number | false {
  if (!order) return false;
  if (order.status !== 'picked_up' && order.status !== 'in_transit') {
    return false;
  }
  if (isRateLimited()) return POLL_ORDER_BACKOFF_MS;
  return POLL_ORDER_LOCATION_MS;
}

export function useOrderUpdates(orderId: string | undefined) {
  const queryClient = useQueryClient();
  const sseFailedRef = useRef(false);

  const query = useQuery({
    queryKey: queryKeys.orders.detail(orderId ?? ''),
    queryFn: () => getOrder(orderId!),
    enabled: Boolean(orderId),
    refetchInterval: (q) => pollInterval(q.state.data as Order | undefined),
  });

  const order = query.data as Order | undefined;
  const useLocationPoll =
    Boolean(orderId) &&
    isTrackable(order) &&
    (!ENABLE_ORDER_SSE || sseFailedRef.current);

  useQuery({
    queryKey: [...queryKeys.orders.detail(orderId ?? ''), 'location'] as const,
    queryFn: async () => {
      const snapshot = await getOrderLocation(orderId!);
      queryClient.setQueryData<Order>(queryKeys.orders.detail(orderId!), (old: Order | undefined) =>
        old
          ? {
              ...old,
              vehicle: snapshot.vehicle ?? old.vehicle,
              driver: snapshot.driver ?? old.driver,
              driverLocation: snapshot.driverLocation ?? old.driverLocation,
            }
          : old,
      );
      return snapshot;
    },
    enabled: useLocationPoll,
    refetchInterval: locationPollInterval(order),
  });

  useEffect(() => {
    if (!orderId || !ENABLE_ORDER_SSE) return;
    const current = queryClient.getQueryData<Order>(
      queryKeys.orders.detail(orderId),
    );
    if (!isTrackable(current)) return;

    const ac = new AbortController();
    let cancelled = false;

    const loop = async () => {
      while (!cancelled && !ac.signal.aborted) {
        try {
          await connectOrderEvents(
            orderId,
            (event) => handleOrderRealtimeEvent(event, queryClient),
            ac.signal,
          );
          if (!cancelled) await new Promise((r) => setTimeout(r, 2000));
        } catch {
          if (ac.signal.aborted || cancelled) break;
          sseFailedRef.current = true;
          await new Promise((r) => setTimeout(r, 3000));
        }
      }
    };

    void loop();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [orderId, order?.status, queryClient]);

  return query;
}
